import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTree, useTreePeople } from '../contexts/TreeContext';
import LoginPage from './LoginPage';
import Sidebar from './Sidebar';
import FamilyTreeCanvas from './FamilyTreeCanvas';
import PersonDetailPanel from './PersonDetailPanel';
import AddPersonModal from './AddPersonModal';
import EditRelationshipsModal from './EditRelationshipsModal';
import MembersPanel from './MembersPanel';
import WelcomeModal from './WelcomeModal';
import TreeWizard from './TreeWizard';
import InviteNotification from './InviteNotification';
import AdminDashboard from './AdminDashboard';
import QuotaNudgeBanner from './QuotaNudgeBanner';
import UpgradeModal from './UpgradeModal';
import SubscriptionDetailsModal from './SubscriptionDetailsModal';
import DeleteAccountModal from './DeleteAccountModal';
import ConsentSettingsModal from './ConsentSettingsModal';
import { initConsent } from '../utils/consent';
import AccessWall from './AccessWall';
import InviteLanding from './InviteLanding';
import useMobile from '../utils/useMobile';
import { peekInviteToken } from '../utils/inviteLink';
import { lookupInvite } from '../utils/api';
import { Icons } from './Icons';
import { getInitials } from '../utils/helpers';
import { identify, setTier, track } from '../utils/analytics';

const ADMIN_UID = process.env.REACT_APP_ADMIN_UID;

export default function App() {
  const { user, emailVerified, loading: authLoading, reloadUser, resendVerificationEmail, logout } = useAuth();
  const { loadingTree, userRole, editablePersonIds, userTrees, activeTreeId, switchTree, peopleCount, inviteJoinError, dismissInviteJoinError } = useTree();
  const { people } = useTreePeople();
  const { maxPeople, tier } = useSubscription();

  const canAddPeople = userRole === 'owner' || userRole === 'admin';
  const canEditPerson = useCallback((personId) => {
    if (userRole === 'owner' || userRole === 'admin') return true;
    if (userRole === 'contributor') return editablePersonIds.has(personId);
    return false;
  }, [userRole, editablePersonIds]);

  const isAdmin = user?.uid === ADMIN_UID;
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);

  const isMobile = useMobile();
  const [activeTab, setActiveTab] = useState('tree');

  const [selectedId, setSelectedId]         = useState(null);
  const [searchQuery, setSearchQuery]       = useState('');
  const [showAddModal, setShowAddModal]     = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [editRelPersonId, setEditRelPersonId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Every route into the upgrade flow goes through here so `source` is
  // recorded rather than assumed. Which of the five entry points people
  // actually use is the question the event exists to answer, and a bare
  // setShowUpgradeModal(true) scattered across the file cannot answer it.
  const openUpgrade = useCallback((source) => {
    track('upgrade_modal_opened', { source });
    setShowUpgradeModal(true);
  }, []);

  const handleAddPerson = useCallback(() => {
    if (peopleCount < maxPeople) setShowAddModal(true);
    else openUpgrade('add_person_cap');
  }, [peopleCount, maxPeople, openUpgrade]);

  // GA4 stitches sessions across devices by user id, and every report worth
  // reading is split by plan. Both are pseudonymous — the uid is allowed, an
  // email address never would be. Cleared on sign-out so a shared browser does
  // not attribute the next person's session to the last one.
  useEffect(() => { identify(user?.uid || null); }, [user]);

  // Settle the advertising-measurement default for anyone who reached the app
  // without passing through kinshiptrees.com, where the question is normally
  // asked. Runs signed out as well as signed in, because the sign-in page is
  // exactly where such a visitor lands. It never overwrites an existing answer,
  // and in Europe it writes nothing: there, the policy says we ask first.
  useEffect(() => { initConsent(); }, []);
  useEffect(() => { if (user) setTier(tier); }, [user, tier]);

  // ── Invite links ──────────────────────────────────────────────────────────
  // Redemption itself lives in TreeContext's bootstrap — it has to happen
  // before the starter-tree decision, and doing it here meant racing it. What
  // is left here is the signed-out landing screen.
  const [inviteToken] = useState(() => peekInviteToken());
  const [invitePreview, setInvitePreview] = useState(null);

  useEffect(() => {
    if (!inviteToken || user) return;
    let cancelled = false;
    lookupInvite(inviteToken)
      .then(result => { if (!cancelled) setInvitePreview(result); })
      .catch(() => { /* leave the ordinary login screen alone */ });
    return () => { cancelled = true; };
  }, [inviteToken, user]);

  // Welcome modal — shown once per user when they have shared (non-owned) trees
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeKey = user ? `kinship_welcome_shown_${user.uid}` : null;

  useEffect(() => {
    if (!welcomeKey || loadingTree || userTrees.length === 0) return;
    const sharedTrees = userTrees.filter(t => t.role !== 'owner');
    if (sharedTrees.length === 0) return;
    if (!localStorage.getItem(welcomeKey)) {
      setShowWelcome(true);
    }
  }, [welcomeKey, loadingTree, userTrees]);

  // Guided wizard — shown once when an owner's tree is empty
  const [showWizard, setShowWizard] = useState(false);
  useEffect(() => {
    if (loadingTree || !canAddPeople || !activeTreeId) return;
    const key = `kinship_wizard_dismissed_${activeTreeId}`;
    // The else matters as much as the if. This only ever opened the wizard, so
    // once it appeared over an empty tree it stayed up after switching to a
    // populated one — an invited user could end up reading "Start with
    // yourself" on top of the 135-person tree they had just joined.
    if (Object.keys(people).length === 0 && !localStorage.getItem(key)) {
      setShowWizard(true);
    } else {
      setShowWizard(false);
    }
  }, [loadingTree, activeTreeId]); // eslint-disable-line

  const handleWizardClose = () => {
    setShowWizard(false);
    if (activeTreeId) localStorage.setItem(`kinship_wizard_dismissed_${activeTreeId}`, '1');
  };

  const handleSelectPerson = useCallback((id) => {
    if (hasUnsavedChanges && id !== selectedId) {
      if (!window.confirm('You have unsaved changes for this person. Discard them?')) return;
    }
    setHasUnsavedChanges(false);
    setSelectedId(id);
    if (isMobile && id) setActiveTab('tree');
  }, [hasUnsavedChanges, selectedId, isMobile]);

  // Loading spinner
  if (authLoading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingInner}>
          <img src="/kt_logo.png" alt="Kinship Trees" style={{ width: 80, height: 80, objectFit: 'contain', marginBottom: 8 }} />
          <p style={{ color: '#2C2418', fontSize: 18, fontFamily: "'Crimson Pro', Georgia, serif", fontWeight: 700, margin: '0 0 20px' }}>Kinship Trees</p>
          <div style={styles.spinner} />
          <p style={{ color: '#8B7D6B', fontSize: 13, marginTop: 14 }}>Loading your family history...</p>
        </div>
      </div>
    );
  }

  // Not logged in → show login, with the invitation's context above it when the
  // visitor followed a link.
  if (!user) {
    return (
      <InviteLanding invite={invitePreview}>
        <LoginPage />
      </InviteLanding>
    );
  }

  // Email/password accounts must verify before entering the app.
  // OAuth users (Google, GitHub, Facebook) always have emailVerified = true
  // so they pass through this gate automatically.
  //
  // **Except someone who arrived by invitation.** They followed a link to see a
  // relative's tree, and walling them off at the last step is the same defect
  // the invite flow has now been fixed for twice: the flow ends on a screen
  // that is not the tree. Holding the link already demonstrates access to the
  // mailbox, which is what verifying demonstrates.
  //
  // This is safe because it grants nothing but reading. The API refuses every
  // write from an unverified caller — authz.can_write_all requires
  // email_verified, and so does the contributor path — so the exemption cannot
  // be used to change anyone's tree, and it is enforced server-side rather than
  // by this component.
  //
  // Membership of a tree they do not own is the test, rather than "is a token
  // pending", so it still holds on later logins once the token is spent.
  const isEmailPasswordUser = user.providerData?.[0]?.providerId === 'password';
  const wasInvited = userTrees.some(t => t.role !== 'owner');
  if (isEmailPasswordUser && !emailVerified && !wasInvited) {
    // While the tree list is still loading there is no way to tell an invited
    // user from an ordinary one, and guessing wrong flashes the wall in front of
    // someone who should never see it.
    if (loadingTree) {
      return (
        <div style={styles.loadingScreen}>
          <div style={styles.loadingInner}>
            <img src="/kt_logo.png" alt="Kinship Trees" style={{ width: 80, height: 80, objectFit: 'contain', marginBottom: 8 }} />
            <p style={{ color: '#2C2418', fontSize: 18, fontFamily: "'Crimson Pro', Georgia, serif", fontWeight: 700, margin: '0 0 20px' }}>Kinship Trees</p>
            <div style={styles.spinner} />
          </div>
        </div>
      );
    }
    return <EmailVerificationScreen onReload={reloadUser} onResend={resendVerificationEmail} onLogout={logout} email={user.email} />;
  }

  // Admin dashboard
  if (isAdmin && showAdminDashboard) {
    return <AdminDashboard onBack={() => setShowAdminDashboard(false)} />;
  }

  // ── Shared modal/overlay helpers ──
  const sharedModals = (
    <>
      {canAddPeople && showAddModal && (
        <AddPersonModal onClose={() => setShowAddModal(false)} existingPeople={people} />
      )}
      {editRelPersonId && (
        <EditRelationshipsModal personId={editRelPersonId} onClose={() => setEditRelPersonId(null)} />
      )}
      {showMembersPanel && <MembersPanel onClose={() => setShowMembersPanel(false)} />}
      {showWizard && <TreeWizard onClose={handleWizardClose} />}
      {showWelcome && (() => {
        const sharedTrees = userTrees.filter(t => t.role !== 'owner');
        const ownedTree   = userTrees.find(t => t.role === 'owner');
        const dismiss = () => { localStorage.setItem(welcomeKey, '1'); setShowWelcome(false); };
        return (
          <WelcomeModal
            sharedTrees={sharedTrees}
            ownedTree={ownedTree}
            onStartOwn={() => { if (ownedTree) switchTree(ownedTree.treeId); dismiss(); }}
            onExploreShared={() => { switchTree(sharedTrees[0].treeId); dismiss(); }}
          />
        );
      })()}
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
      {showSubscriptionModal && <SubscriptionDetailsModal onClose={() => setShowSubscriptionModal(false)} />}
      {showDeleteAccount && <DeleteAccountModal onClose={() => setShowDeleteAccount(false)} />}
      {showCookieSettings && <ConsentSettingsModal onClose={() => setShowCookieSettings(false)} />}
      <InviteNotification />

      {/* An invite link that the server refused — already used, expired, or the
          caller is a member already. Worth stating plainly rather than dropping
          the visitor into the app with no explanation of why nothing changed. */}
      {inviteJoinError && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 400, maxWidth: 420, padding: '12px 16px', borderRadius: 10,
          background: '#2C2418', color: '#F5EDE3', fontSize: 13, lineHeight: 1.5,
          fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        }}>
          <span style={{ flex: 1 }}>{inviteJoinError}</span>
          <button
            onClick={dismissInviteJoinError}
            style={{
              background: 'none', border: 'none', color: '#C4975A',
              cursor: 'pointer', fontSize: 13, fontWeight: 700,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Dismiss
          </button>
        </div>
      )}
      <AccessWall onUpgradeClick={() => openUpgrade('access_wall')} />
      {canAddPeople && <QuotaNudgeBanner onUpgradeClick={() => openUpgrade('quota_nudge')} />}
    </>
  );

  // ── Mobile layout ──
  if (isMobile) {
    return (
      <div style={{ height: '100dvh', width: '100vw', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>

        {/* Tab content */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* Tree tab */}
          {activeTab === 'tree' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
              <FamilyTreeCanvas
                onSelectPerson={handleSelectPerson}
                selectedId={selectedId}
                canEdit={canAddPeople}
              />

              {/* Add Person FAB */}
              {canAddPeople && (
                <button
                  onClick={handleAddPerson}
                  style={{
                    position: 'absolute', bottom: 20, right: 20, zIndex: 5,
                    width: 52, height: 52, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #C4975A 0%, #A07840 100%)',
                    border: 'none', cursor: 'pointer', color: '#2C2418',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(196,151,90,0.45)',
                  }}
                >
                  <Icons.Plus size={22} />
                </button>
              )}

              {/* Person detail bottom sheet */}
              {selectedId && people[selectedId] && (
                <>
                  <div
                    style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10 }}
                    onClick={() => handleSelectPerson(null)}
                  />
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: '88%',
                    zIndex: 20, background: '#FDFBF7',
                    borderRadius: '20px 20px 0 0', overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 -4px 24px rgba(0,0,0,0.2)',
                  }}>
                    <div style={{ padding: '10px 0 6px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                      <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D5CAB8' }} />
                    </div>
                    <PersonDetailPanel
                      personId={selectedId}
                      onClose={() => handleSelectPerson(null)}
                      onEditRelationships={() => setEditRelPersonId(selectedId)}
                      canEdit={canEditPerson(selectedId)}
                      onUnsavedChange={setHasUnsavedChanges}
                      isMobile={true}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* People tab */}
          {activeTab === 'people' && (
            <Sidebar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onSelectPerson={handleSelectPerson}
              selectedId={selectedId}
              onAddPerson={handleAddPerson}
              onManageAccess={() => setShowMembersPanel(true)}
              canEdit={canAddPeople}
              isMobile={true}
            />
          )}

          {/* Me tab */}
          {activeTab === 'me' && (
            <MeTab
              onManageAccess={() => setShowMembersPanel(true)}
              isAdmin={isAdmin}
              onShowAdmin={() => setShowAdminDashboard(true)}
              onUpgradeClick={() => openUpgrade('me_tab')}
              onManageSubscription={() => setShowSubscriptionModal(true)}
              onDeleteAccount={() => setShowDeleteAccount(true)}
              onCookieSettings={() => setShowCookieSettings(true)}
            />
          )}
        </div>

        {/* Bottom tab bar */}
        <div style={{
          background: '#2C2418', borderTop: '1px solid rgba(196,151,90,0.2)',
          display: 'flex', flexShrink: 0,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}>
          <MobileTabBtn icon={<Icons.Tree size={22} />} label="Tree" active={activeTab === 'tree'} onClick={() => setActiveTab('tree')} />
          <MobileTabBtn icon={<Icons.Users size={22} />} label="People" active={activeTab === 'people'} onClick={() => setActiveTab('people')} />
          <MobileTabBtn icon={<Icons.Person size={22} />} label="Me" active={activeTab === 'me'} onClick={() => setActiveTab('me')} />
        </div>

        {sharedModals}
        {loadingTree && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(253,251,247,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, backdropFilter: 'blur(2px)' }}>
            <div style={styles.spinner} />
          </div>
        )}
      </div>
    );
  }

  // ── Desktop layout ──
  return (
    <div style={styles.appShell}>
      <Sidebar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSelectPerson={handleSelectPerson}
        selectedId={selectedId}
        onAddPerson={handleAddPerson}
        onManageAccess={() => setShowMembersPanel(true)}
        onUpgradeClick={() => openUpgrade('sidebar')}
        onManageSubscription={() => setShowSubscriptionModal(true)}
        onDeleteAccount={() => setShowDeleteAccount(true)}
        onCookieSettings={() => setShowCookieSettings(true)}
        canEdit={canAddPeople}
        isAdmin={isAdmin}
        onShowAdmin={() => setShowAdminDashboard(true)}
      />

      <FamilyTreeCanvas
        onSelectPerson={handleSelectPerson}
        selectedId={selectedId}
        canEdit={canAddPeople}
      />

      {selectedId && people[selectedId] && (
        <PersonDetailPanel
          personId={selectedId}
          onClose={() => handleSelectPerson(null)}
          onEditRelationships={() => setEditRelPersonId(selectedId)}
          canEdit={canEditPerson(selectedId)}
          onUnsavedChange={setHasUnsavedChanges}
        />
      )}

      {sharedModals}

      {/* Loading overlay for tree data */}
      {loadingTree && (
        <div style={styles.treeLoadingOverlay}>
          <div style={styles.spinner} />
        </div>
      )}
    </div>
  );
}

// ── Mobile helper components ──

function MobileTabBtn({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 4, background: 'none', border: 'none',
        cursor: 'pointer', padding: '10px 0',
        color: active ? '#C4975A' : '#6B5E4F',
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {icon}
      <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, letterSpacing: 0.3 }}>{label}</span>
    </button>
  );
}

const ROLE_COLORS = {
  owner:       { bg: 'rgba(196,151,90,0.25)',  color: '#C4975A' },
  admin:       { bg: 'rgba(109,174,114,0.25)', color: '#6DAE72' },
  contributor: { bg: 'rgba(91,138,196,0.25)',  color: '#5B8AC4' },
  viewer:      { bg: 'rgba(160,147,127,0.25)', color: '#A0937F' },
};

function MeTab({ onManageAccess, isAdmin, onShowAdmin, onUpgradeClick, onManageSubscription, onDeleteAccount, onCookieSettings }) {
  const { user, logout } = useAuth();
  const { userRole, userTrees, activeTreeId, switchTree } = useTree();
  const { tier } = useSubscription();

  return (
    <div style={{
      flex: 1, background: '#2C2418', color: '#F5EDE3',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'DM Sans', sans-serif", overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{ padding: '28px 20px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/kt_logo.png" alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'contain' }} />
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontFamily: "'Crimson Pro', Georgia, serif", fontWeight: 700 }}>Kinship Trees</h1>
          <p style={{ margin: 0, fontSize: 11, color: '#A0937F', letterSpacing: 1.2, textTransform: 'uppercase' }}>Family History</p>
        </div>
      </div>

      {/* User info card */}
      <div style={{ margin: '0 16px 20px', background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, border: '1px solid rgba(196,151,90,0.15)' }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          background: 'rgba(196,151,90,0.2)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, color: '#C4975A', overflow: 'hidden',
        }}>
          {user?.photoURL
            ? <img src={user.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : getInitials(user?.displayName || user?.email || '?')
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.displayName || 'User'}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: '#8B7D6B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.email}
          </p>
        </div>
      </div>

      {/* Trees section */}
      <div style={{ padding: '0 16px 20px' }}>
        <p style={{ margin: '0 0 10px 4px', fontSize: 11, color: '#A0937F', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700 }}>
          Your Trees
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {userTrees.map(t => {
            const rc = ROLE_COLORS[t.role] || ROLE_COLORS.viewer;
            const isActive = t.treeId === activeTreeId;
            return (
              <button
                key={t.treeId}
                onClick={() => switchTree(t.treeId)}
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: 10,
                  background: isActive ? 'rgba(196,151,90,0.15)' : 'rgba(255,255,255,0.05)',
                  border: isActive ? '1px solid rgba(196,151,90,0.4)' : '1px solid rgba(255,255,255,0.08)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                  color: '#F5EDE3', fontFamily: "'DM Sans', sans-serif",
                }}
              >
                <Icons.Tree size={16} />
                <span style={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.name}
                </span>
                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: rc.bg, color: rc.color, textTransform: 'uppercase', letterSpacing: 0.6, flexShrink: 0 }}>
                  {t.role}
                </span>
                {isActive && <span style={{ color: '#C4975A', flexShrink: 0 }}><Icons.Check size={14} /></span>}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ height: 1, background: 'rgba(196,151,90,0.15)', margin: '0 16px 16px' }} />

      {/* Actions */}
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {tier === 'paid' ? (
          <MeActionBtn icon={<Icons.Star size={16} />} label="Manage Subscription" onClick={onManageSubscription} subtle />
        ) : (
          <MeActionBtn icon={<Icons.Star size={16} />} label="Upgrade to Paid" onClick={onUpgradeClick} />
        )}
        {userRole && (
          <MeActionBtn icon={<Icons.Users size={16} />} label={userRole === 'owner' ? 'Manage Access' : 'Invite Someone'} onClick={onManageAccess} />
        )}
        {isAdmin && (
          <MeActionBtn icon={<Icons.Edit size={16} />} label="Admin Dashboard" onClick={onShowAdmin} subtle />
        )}
      </div>

      <div style={{ flex: 1 }} />

      {/* Sign out */}
      <div style={{ padding: '16px' }}>
        <button onClick={logout} style={{
          width: '100%', padding: '13px 0', borderRadius: 10,
          background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.3)',
          color: '#E07B6F', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <Icons.Logout size={16} /> Sign Out
        </button>
        {/* Deliberately plain text rather than a button: reachable by anyone who
            goes looking for it, and not something to land on by accident. */}
        <button onClick={onDeleteAccount} style={{
          width: '100%', marginTop: 12, padding: 0, background: 'none', border: 'none',
          color: '#8B7D6B', fontSize: 12, textDecoration: 'underline', cursor: 'pointer',
          fontFamily: "'DM Sans', sans-serif",
        }}>
          Delete my account
        </button>
        {/* The privacy policy promises cookie settings on every page. This is
            the mobile half of keeping that promise; Sidebar.js is the other. */}
        <button onClick={onCookieSettings} style={{
          width: '100%', marginTop: 10, padding: 0, background: 'none', border: 'none',
          color: '#8B7D6B', fontSize: 12, textDecoration: 'underline', cursor: 'pointer',
          fontFamily: "'DM Sans', sans-serif",
        }}>
          Cookie settings
        </button>
      </div>
    </div>
  );
}

function MeActionBtn({ icon, label, onClick, subtle }) {
  return (
    <button onClick={onClick} style={{
      width: '100%', padding: '13px 14px', borderRadius: 10,
      background: subtle ? 'rgba(107,94,79,0.15)' : 'rgba(196,151,90,0.12)',
      border: subtle ? '1px solid rgba(107,94,79,0.3)' : '1px solid rgba(196,151,90,0.25)',
      color: subtle ? '#A0937F' : '#C4975A',
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {icon}
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
    </button>
  );
}

function EmailVerificationScreen({ onReload, onResend, onLogout, email }) {
  const [resent, setResent] = useState(false);
  const [checking, setChecking] = useState(false);

  const handleResend = async () => {
    await onResend();
    setResent(true);
    setTimeout(() => setResent(false), 5000);
  };

  const handleCheck = async () => {
    setChecking(true);
    await onReload();
    setChecking(false);
  };

  return (
    <div style={{ height: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAF7F2', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 420, width: '90%', textAlign: 'center' }}>
        <img src="/kt_logo.png" alt="Kinship Trees" style={{ width: 64, height: 64, objectFit: 'contain', marginBottom: 16 }} />
        <h1 style={{ margin: '0 0 8px', fontSize: 24, fontFamily: "'Crimson Pro', Georgia, serif", fontWeight: 700, color: '#2C2418' }}>Check your email</h1>
        <p style={{ margin: '0 0 6px', fontSize: 14, color: '#6B5E4F', lineHeight: 1.6 }}>
          We sent a verification link to
        </p>
        <p style={{ margin: '0 0 24px', fontSize: 14, fontWeight: 700, color: '#2C2418' }}>{email}</p>
        <p style={{ margin: '0 0 28px', fontSize: 13, color: '#8B7D6B', lineHeight: 1.6 }}>
          Click the link in the email, then come back and press the button below. Check your spam folder if you don't see it.
        </p>
        <button
          onClick={handleCheck}
          disabled={checking}
          style={{ width: '100%', padding: '12px 0', borderRadius: 8, background: 'linear-gradient(135deg, #C4975A 0%, #A07840 100%)', border: 'none', color: '#2C2418', fontSize: 14, fontWeight: 700, cursor: checking ? 'default' : 'pointer', marginBottom: 10, opacity: checking ? 0.7 : 1, fontFamily: "'DM Sans', sans-serif" }}
        >
          {checking ? 'Checking…' : "I've verified my email"}
        </button>
        <button
          onClick={handleResend}
          style={{ width: '100%', padding: '10px 0', borderRadius: 8, background: 'transparent', border: '1px solid rgba(196,151,90,0.4)', color: '#C4975A', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}
        >
          {resent ? 'Email sent!' : 'Resend verification email'}
        </button>
        <button
          onClick={onLogout}
          style={{ background: 'none', border: 'none', color: '#A0937F', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', fontFamily: "'DM Sans', sans-serif" }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

const styles = {
  appShell: {
    display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden',
    fontFamily: "'DM Sans', sans-serif",
  },
  loadingScreen: {
    height: '100vh', width: '100vw',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#FAF7F2',
  },
  loadingInner: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  spinner: {
    width: 32, height: 32,
    border: '3px solid #E8E0D4',
    borderTop: '3px solid #C4975A',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  treeLoadingOverlay: {
    position: 'fixed', top: 0, left: 280, right: 0, bottom: 0,
    background: 'rgba(253,251,247,0.7)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 50,
    backdropFilter: 'blur(2px)',
  },
};
